import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { messageService } from '../../services/messageService';
import { getNewHires, NewHire } from '../../services/hireService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';
import { useTheme } from '@mui/material/styles';
import HireConversationHistory from './HireConversationHistory';

interface SubTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function SubTabPanel(props: SubTabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hire-subtab-${index}`}
      aria-labelledby={`hire-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const HireMessenger: React.FC = () => {
  const [subTab, setSubTab] = useState(0);
  const [hires, setHires] = useState<NewHire[]>([]);
  const [selectedHires, setSelectedHires] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [progressStages, setProgressStages] = useState<ProgressStage[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const theme = useTheme();

  useEffect(() => {
    fetchHires();
    fetchProgressStages();
  }, []);

  const fetchHires = async () => {
    try {
      setLoading(true);
      const hiresData = await getNewHires();
      // Filter to only show hires with mobile numbers
      const hiresWithPhones = hiresData.filter(hire => hire.mobileNumber);
      setHires(hiresWithPhones);
    } catch (error: any) {
      console.error('Error fetching hires:', error);
      showSnackbar('Error loading hires: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressStages = async () => {
    try {
      const stages = await progressStageService.getCurrentUserProgressStages();
      setProgressStages(stages);
    } catch (error: any) {
      console.error('Error fetching progress stages:', error);
      // Continue with empty stages array - filter will still work with 'all' option
    }
  };

  const handleSelectAll = () => {
    if (selectedHires.size === filteredHires.length) {
      setSelectedHires(new Set());
    } else {
      setSelectedHires(new Set(filteredHires.map(h => h.id)));
    }
  };

  const handleSelectHire = (hireId: string) => {
    const newSelected = new Set(selectedHires);
    if (newSelected.has(hireId)) {
      newSelected.delete(hireId);
    } else {
      newSelected.add(hireId);
    }
    setSelectedHires(newSelected);
  };

  const handleSendBulkMessage = async () => {
    if (selectedHires.size === 0 || !message.trim()) {
      showSnackbar('Please select at least one hire and enter a message', 'error');
      return;
    }

    try {
      setSending(true);
      setSendProgress({ current: 0, total: selectedHires.size });

      const hireIds = Array.from(selectedHires);
      const response = await messageService.sendBulkHireSMS({
        hireIds,
        message: message.trim()
      });

      if (response.success) {
        showSnackbar(response.message, 'success');
        setMessage('');
        setSelectedHires(new Set());
        setSendProgress({ current: 0, total: 0 });
      } else {
        const failedCount = response.results.filter(r => !r.success).length;
        const successCount = response.results.filter(r => r.success).length;
        if (failedCount > 0) {
          showSnackbar(`${successCount} sent successfully, ${failedCount} failed. ${response.message}`, 'error');
        } else {
          showSnackbar(response.message, 'success');
        }
        setSendProgress({ current: 0, total: 0 });
      }
    } catch (error: any) {
      console.error('Error sending bulk message:', error);
      showSnackbar('Error sending messages: ' + error.message, 'error');
      setSendProgress({ current: 0, total: 0 });
    } finally {
      setSending(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredHires = hires.filter(hire => {
    // Search filter
    const matchesSearch = search === '' || 
      hire.fullName.toLowerCase().includes(search.toLowerCase()) ||
      hire.email.toLowerCase().includes(search.toLowerCase()) ||
      (hire.mobileNumber && hire.mobileNumber.toLowerCase().includes(search.toLowerCase()));
    
    // Category filter (by progress stage)
    const matchesCategory = categoryFilter === 'all' || 
      (categoryFilter !== 'all' && hire.progress === Number(categoryFilter));
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Sub-tabs: Send Message | Conversation History */}
      <Tabs
        value={subTab}
        onChange={(_e, v) => setSubTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.95rem',
          },
        }}
      >
        <Tab label="Send Message" />
        <Tab label="Conversation History" />
      </Tabs>

      {/* ---- Tab 0: Send Message (existing view) ---- */}
      <SubTabPanel value={subTab} index={0}>
      {/* Message Input Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Send Message to Selected Hires
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending || selectedHires.size === 0}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSendBulkMessage}
            disabled={!message.trim() || selectedHires.size === 0 || sending}
            sx={{ minWidth: 120, height: 'fit-content' }}
          >
            {sending ? 'Sending...' : `Send (${selectedHires.size})`}
          </Button>
        </Box>
        {sending && sendProgress.total > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={(sendProgress.current / sendProgress.total) * 100} 
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="textSecondary">
              Sending {sendProgress.current} of {sendProgress.total} messages...
            </Typography>
          </Box>
        )}
        {selectedHires.size > 0 && (
          <Box sx={{ mt: 2 }}>
            <Chip 
              label={`${selectedHires.size} hire(s) selected`}
              color="primary"
              onDelete={() => setSelectedHires(new Set())}
            />
          </Box>
        )}
      </Paper>

      {/* Search and Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search hires..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Hires</MenuItem>
            {progressStages.map((stage) => (
              <MenuItem key={stage.value} value={stage.value.toString()}>
                {stage.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchHires}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          onClick={handleSelectAll}
          disabled={filteredHires.length === 0}
        >
          {selectedHires.size === filteredHires.length ? 'Deselect All' : 'Select All'}
        </Button>
      </Box>

      {/* Hires Table */}
      <TableContainer 
        component={Paper} 
        elevation={2}
        sx={{ 
          maxHeight: '600px',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 60,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                <Checkbox
                  indeterminate={selectedHires.size > 0 && selectedHires.size < filteredHires.length}
                  checked={filteredHires.length > 0 && selectedHires.size === filteredHires.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 150,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                Name
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 150,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                Phone
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 200,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                Email
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 100,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                State
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600, 
                minWidth: 120,
                backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                Created
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {              filteredHires.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {search || categoryFilter !== 'all' 
                      ? 'No hires found matching your filters' 
                      : 'No hires with phone numbers found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredHires.map((hire) => (
                <TableRow 
                  key={hire.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.08)' 
                        : 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                  onClick={() => handleSelectHire(hire.id)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedHires.has(hire.id)}
                      onChange={() => handleSelectHire(hire.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      {hire.fullName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {hire.mobileNumber || 'No phone'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {hire.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {hire.state || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                      {formatDate(hire.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </SubTabPanel>

      {/* ---- Tab 1: Conversation History ---- */}
      <SubTabPanel value={subTab} index={1}>
        <HireConversationHistory />
      </SubTabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HireMessenger;

